"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ArrowLeft, Pencil, Plus, Trash2, Video, Link } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  TextField,
  TextareaField,
  SwitchField,
} from "@admin/_components/form-fields";
import { useApiPost } from "@admin/_lib/api-hooks";
import { useSession } from "next-auth/react";

// ── Types ─────────────────────────────────────────────────────────────────────

type VideoEntry = {
  videotype: "1" | "3";
  youtube_link?: string;
  youtube_title?: string;
  youtube_description?: string;
  video_title?: string;
  video_Desc?: string;
  /** URL/path of the uploaded video file for videotype=3 */
  video_url?: string;
};

// ── Schema ────────────────────────────────────────────────────────────────────

const schema = z.object({
  title: z.string().min(1, "Title is required"),
  priority: z.string().min(1, "Priority is required"),
  description: z.string().min(1, "Description is required"),
  status: z.boolean().default(true),
});

type FormValues = z.infer<typeof schema>;

// ── VideoEntryModal ───────────────────────────────────────────────────────────

function VideoEntryModal({
  open,
  onClose,
  editEntry,
  editIndex,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  editEntry?: VideoEntry;
  editIndex?: number;
  onSave: (entry: VideoEntry, editIndex?: number) => void;
}) {
  const isEditMode = editIndex !== undefined;

  const [videotype, setVideotype] = useState<"1" | "3">("1");
  const [youtubeLink, setYoutubeLink] = useState("");
  const [youtubeTitle, setYoutubeTitle] = useState("");
  const [youtubeDescription, setYoutubeDescription] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [videoDesc, setVideoDesc] = useState("");
  const [videoUrl, setVideoUrl] = useState("");

  // Populate fields when modal opens, based on edit vs. add mode
  useEffect(() => {
    if (!open) return;
    if (editEntry) {
      setVideotype(editEntry.videotype);
      setYoutubeLink(editEntry.youtube_link ?? "");
      setYoutubeTitle(editEntry.youtube_title ?? "");
      setYoutubeDescription(editEntry.youtube_description ?? "");
      setVideoTitle(editEntry.video_title ?? "");
      setVideoDesc(editEntry.video_Desc ?? "");
      setVideoUrl(editEntry.video_url ?? "");
    } else {
      setVideotype("1");
      setYoutubeLink("");
      setYoutubeTitle("");
      setYoutubeDescription("");
      setVideoTitle("");
      setVideoDesc("");
      setVideoUrl("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleSave() {
    if (videotype === "1" && !youtubeLink.trim()) {
      toast.error("YouTube link is required");
      return;
    }
    const entry: VideoEntry = { videotype };
    if (videotype === "1") {
      entry.youtube_link = youtubeLink.trim();
      entry.youtube_title = youtubeTitle.trim() || undefined;
      entry.youtube_description = youtubeDescription.trim() || undefined;
    } else {
      entry.video_title = videoTitle.trim() || undefined;
      entry.video_Desc = videoDesc.trim() || undefined;
      entry.video_url = videoUrl.trim() || undefined;
    }
    onSave(entry, editIndex);
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="sm:max-w-lg"
        style={{ background: "hsl(240 20% 10%)", border: "1px solid hsl(240 20% 20%)" }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
            {isEditMode ? "Edit Video Entry" : "Add Video Entry"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Video Type */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Video Type</Label>
            <Select value={videotype} onValueChange={(v) => setVideotype(v as "1" | "3")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Youtube Link</SelectItem>
                <SelectItem value="3">Video File</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* YouTube fields */}
          {videotype === "1" && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">
                  YouTube Link <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={youtubeLink}
                  onChange={(e) => setYoutubeLink(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">YouTube Title</Label>
                <Input
                  value={youtubeTitle}
                  onChange={(e) => setYoutubeTitle(e.target.value)}
                  placeholder="Video title"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">YouTube Description</Label>
                <Input
                  value={youtubeDescription}
                  onChange={(e) => setYoutubeDescription(e.target.value)}
                  placeholder="Video description"
                />
              </div>
            </div>
          )}

          {/* Video File fields */}
          {videotype === "3" && (
            <div className="space-y-3">
              <div
                className="rounded-md px-3 py-2.5 text-xs"
                style={{ background: "hsl(240 20% 16%)", color: "#25aceb", border: "1px solid hsl(240 20% 22%)" }}
              >
                S3 bucket: <strong>divine-infinity-being/video-management/</strong>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Video File URL</Label>
                <Input
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://... or S3 path"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Video Title</Label>
                <Input
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  placeholder="Video title"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Video Description</Label>
                <Input
                  value={videoDesc}
                  onChange={(e) => setVideoDesc(e.target.value)}
                  placeholder="Video description"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <button
            type="button"
            onClick={handleSave}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-all btn-orange"
          >
            {isEditMode ? "Save" : "Add"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Video Form ────────────────────────────────────────────────────────────────

export function VideoForm({ id }: { id?: string }) {
  const router = useRouter();
  const isEdit = !!id;
  const { data: session } = useSession();

  const baseUrl =
    process.env.NEXT_PUBLIC_API_CLOUDFRONT ?? "https://api.divineinfinitebeing.com/";

  const [modalOpen, setModalOpen] = useState(false);
  const [videos, setVideos] = useState<VideoEntry[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | undefined>();
  const [editingEntry, setEditingEntry] = useState<VideoEntry | undefined>();

  // Fetch existing record for edit
  const [record, setRecord] = useState<Record<string, unknown> | null>(null);
  const [isFetching, setIsFetching] = useState(isEdit);

  useEffect(() => {
    if (!isEdit || !session?.token) return;
    setIsFetching(true);
    fetch(`${baseUrl}videomanagement/video-data-fetch`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.token}`,
      },
      body: JSON.stringify({ _id: id }),
    })
      .then((r) => r.json())
      .then((data) => {
        const rec = data.results?.res ?? data.results ?? data;
        setRecord(rec);
        if (Array.isArray(rec.videos)) setVideos(rec.videos as VideoEntry[]);
      })
      .catch(() => toast.error("Failed to load record"))
      .finally(() => setIsFetching(false));
  }, [isEdit, session?.token, id, baseUrl]);

  const addMutation = useApiPost("videomanagement/video-add");
  const editMutation = useApiPost("videomanagement/video-edit");
  const mutation = isEdit ? editMutation : addMutation;

  const form = useForm<FormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema) as any,
    defaultValues: { title: "", priority: "", description: "", status: true },
  });

  useEffect(() => {
    if (record) {
      form.reset({
        title: (record.title as string) ?? "",
        priority: record.priority != null ? String(record.priority) : "",
        description: (record.description as string) ?? "",
        status: record.status === 1 || record.status === true,
      });
    }
  }, [record, form]);

  function openAddModal() {
    setEditingIndex(undefined);
    setEditingEntry(undefined);
    setModalOpen(true);
  }

  function openEditModal(index: number) {
    setEditingIndex(index);
    setEditingEntry(videos[index]);
    setModalOpen(true);
  }

  function handleModalClose() {
    setModalOpen(false);
    setEditingIndex(undefined);
    setEditingEntry(undefined);
  }

  function handleSave(entry: VideoEntry, index?: number) {
    if (index !== undefined) {
      setVideos((prev) => prev.map((v, i) => (i === index ? entry : v)));
    } else {
      setVideos((prev) => [...prev, entry]);
    }
  }

  function removeVideo(index: number) {
    setVideos((prev) => prev.filter((_, i) => i !== index));
  }

  async function onSubmit(values: FormValues) {
    try {
      const body: Record<string, unknown> = {
        title: values.title,
        priority: parseFloat(values.priority) || 0,
        description: values.description,
        status: values.status ? 1 : 0,
        videos,
      };
      if (isEdit) body._id = id;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await mutation.mutateAsync(body as any);
      toast.success(isEdit ? "Video updated successfully" : "Video created successfully");
      router.push("/admin/videos");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Operation failed");
    }
  }

  if (isEdit && isFetching) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-64 animate-pulse rounded-md" style={{ background: "hsl(240 20% 16%)" }} />
        <div className="h-96 animate-pulse rounded-xl" style={{ background: "hsl(240 20% 16%)" }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="shrink-0" onClick={() => router.back()}>
          <ArrowLeft className="size-4" />
        </Button>
        <div className="h-6 w-1 rounded-full shrink-0" style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }} />
        <div>
          <h1 className="text-xl font-semibold text-foreground">
            {isEdit ? "Edit Video" : "Add Video"}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isEdit ? "Update video details" : "Create a new video entry"}
          </p>
        </div>
      </div>

      {/* Main Form Card */}
      <Card>
        <CardHeader className="border-b" style={{ borderColor: "hsl(240 20% 20%)" }}>
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
            Video Details
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <TextField control={form.control} name="title" label="Title" placeholder="Enter video title" />
                <TextField control={form.control} name="priority" label="Priority" type="number" placeholder="e.g. 1" />
                <div className="md:col-span-2">
                  <TextareaField control={form.control} name="description" label="Description" placeholder="Video description..." />
                </div>
                <div className="md:col-span-2">
                  <SwitchField control={form.control} name="status" label="Published" description="Make this video visible" />
                </div>
              </div>

              {/* Video Entries Section */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <span className="h-3.5 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
                    Video Entries
                  </h3>
                  <button
                    type="button"
                    onClick={openAddModal}
                    className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold transition-all btn-orange"
                  >
                    <Plus className="size-3" />
                    Add Video
                  </button>
                </div>

                {videos.length === 0 ? (
                  <div
                    className="flex flex-col items-center justify-center rounded-lg py-8 text-center text-muted-foreground"
                    style={{ border: "1px dashed hsl(240 20% 25%)" }}
                  >
                    <Video className="size-8 mb-2 opacity-30" />
                    <p className="text-xs">No videos added yet. Click &quot;Add Video&quot; to get started.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {videos.map((v, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-3 rounded-lg px-4 py-3"
                        style={{ background: "hsl(240 20% 14%)", border: "1px solid hsl(240 20% 22%)" }}
                      >
                        <div className="mt-0.5 shrink-0">
                          {v.videotype === "1" ? (
                            <Link className="size-4" style={{ color: "#ffa800" }} />
                          ) : (
                            <Video className="size-4" style={{ color: "#25aceb" }} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0 space-y-0.5">
                          <p className="text-xs font-medium">
                            {v.videotype === "1" ? "YouTube Link" : "Video File"}
                          </p>
                          {v.videotype === "1" ? (
                            <>
                              <p className="text-xs text-muted-foreground truncate">{v.youtube_link}</p>
                              {v.youtube_title && (
                                <p className="text-xs text-muted-foreground">{v.youtube_title}</p>
                              )}
                            </>
                          ) : (
                            <>
                              {v.video_title && (
                                <p className="text-xs text-muted-foreground">{v.video_title}</p>
                              )}
                              {v.video_url && (
                                <p className="text-xs text-muted-foreground truncate">{v.video_url}</p>
                              )}
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => openEditModal(i)}
                            title="Edit"
                            className="rounded p-1 text-muted-foreground transition-colors hover:text-[#25aceb]"
                          >
                            <Pencil className="size-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeVideo(i)}
                            title="Remove"
                            className="rounded p-1 text-muted-foreground transition-colors hover:text-destructive"
                          >
                            <Trash2 className="size-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed btn-orange"
                >
                  {mutation.isPending ? "Saving..." : isEdit ? "Update" : "Create"}
                </button>
                <Button type="button" variant="outline" onClick={() => router.back()}>
                  Cancel
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <VideoEntryModal
        open={modalOpen}
        onClose={handleModalClose}
        editEntry={editingEntry}
        editIndex={editingIndex}
        onSave={handleSave}
      />
    </div>
  );
}
